// "Cannot sign in? Write to support@…" — with the warning infra and the owner
// asked for: no medical records or attachments to this address. Shown only
// where the in-app support ticket cannot be reached (before sign-in), and only
// when this stack has a support address (GET /api/support-contact).
//
//   variant="signin"  under the sign-in form
//   variant="help"    the landing page ("Need help?")
import { useTranslation } from 'react-i18next';
import { Mail, ShieldAlert } from 'lucide-react';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import { useSupportEmail } from '../../utils/supportContact';

export default function SupportContactNote({ variant = 'signin', className = '' }) {
  const { t } = useTranslation();
  const axiosInstance = useAxiosAuth();
  const email = useSupportEmail(() => axiosInstance?.get(`${baseURL}/support-contact`));
  if (!email) return null;
  const lead = variant === 'help'
    ? t('supportContact.need_help', 'Need help with your account?')
    : t('supportContact.cannot_sign_in', 'Cannot sign in?');
  return (
    <div
      data-testid={`support-contact-${variant}`}
      className={`rounded-xl border border-[#C7D6D5] bg-[#F4F9F9] px-4 py-3 text-left ${className}`}
    >
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-gray-700">
        <Mail className="h-4 w-4 flex-shrink-0 text-[#2D8A8A]" aria-hidden="true" />
        <span className="font-semibold text-[#2D8A8A]">{lead}</span>
        <span>{t('supportContact.write_to', 'Write to')}</span>
        <a
          href={`mailto:${email}`}
          data-testid="support-contact-email"
          className="font-semibold text-[#2D8A8A] underline-offset-2 hover:underline"
        >
          {email}
        </a>
      </p>
      <p className="mt-1.5 flex gap-1.5 text-xs leading-relaxed text-gray-600" data-testid="support-contact-warning">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#D7A05C]" aria-hidden="true" />
        <span>
          <strong className="font-semibold text-gray-700">
            {t('supportContact.warning', 'Do not send medical records, reports, prescriptions or any health-related documents or attachments to this address.')}
          </strong>{' '}
          {t('supportContact.scope', 'This mailbox is only for help with signing in and using the app. For medical questions, please contact your doctor or hospital.')}
        </span>
      </p>
    </div>
  );
}
